import { useState, useEffect, useRef, KeyboardEvent } from 'react';

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelectLocation: (location: { address: string; lat: number; lng: number }) => void;
  placeholder?: string;
  id?: string;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelectLocation,
  placeholder = 'e.g., City Hospital, London',
  id = 'address-autocomplete',
}: AddressAutocompleteProps) {
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortController = useRef<AbortController | null>(null);

  // Fetch address suggestions from Nominatim
  const fetchAddresses = async (query: string) => {
    if (query.length < 3) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    // Cancel previous request
    if (abortController.current) {
      abortController.current.abort();
    }

    abortController.current = new AbortController();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
          `q=${encodeURIComponent(query)}&` +
          `format=json&` +
          `countrycodes=gb&` +
          `limit=5&` +
          `addressdetails=1`,
        {
          signal: abortController.current.signal,
          headers: {
            'User-Agent': 'Kin/0.1 (family coordination app)',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch addresses');
      }

      const data: NominatimResult[] = await response.json();
      setResults(data);
      setShowDropdown(data.length > 0);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError('Unable to search addresses. Please check your connection.');
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchAddresses(value);
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value]);

  // Handle selection
  const handleSelect = (result: NominatimResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    onChange(result.display_name);
    onSelectLocation({
      address: result.display_name,
      lat,
      lng,
    });

    setShowDropdown(false);
    setSelectedIndex(-1);
  };

  // Keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
      setSelectedIndex(-1);
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  return (
    <div className="relative">
      <input
        type="text"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="input"
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={`${id}-listbox`}
        aria-activedescendant={
          selectedIndex >= 0 ? `${id}-option-${selectedIndex}` : undefined
        }
      />

      {/* Loading indicator */}
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
        </div>
      )}

      {/* Error message */}
      {error && !loading && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {/* Dropdown results */}
      {showDropdown && results.length > 0 && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        >
          {results.map((result, index) => (
            <li
              key={index}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              onClick={() => handleSelect(result)}
              className={`cursor-pointer px-4 py-3 text-sm ${
                index === selectedIndex
                  ? 'bg-primary-100 text-primary-900'
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              {result.display_name}
            </li>
          ))}
        </ul>
      )}

      {/* No results message */}
      {showDropdown && results.length === 0 && value.length >= 3 && !loading && (
        <div className="absolute z-10 mt-1 w-full rounded-md bg-white p-3 shadow-lg ring-1 ring-black ring-opacity-5">
          <p className="text-sm text-gray-600">
            No addresses found. Try a different search.
          </p>
        </div>
      )}
    </div>
  );
}
